import {
  ApiOutlined,
  CopyOutlined,
  DeleteOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMemo } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import DefaultToast from '@/components/Toast/DefaultToast';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useToastStore from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { clipboardCopy } from '@/utils/util';

import SettingHeader from '../Header';

const APISettingBody = () => {
  const color = useColor();
  const {
    currWorkspace,
    fetchWorkspaceAPI,
    deleteWorkspaceAPI,
    currWorkspaceMembers,
  } = useWorkspaceStore((state) => {
    return {
      currWorkspace: state.currWorkspace,
      fetchWorkspaceAPI: state.fetchWorkspaceAPI,
      deleteWorkspaceAPI: state.deleteWorkspaceAPI,
      currWorkspaceMembers: state.currWorkspaceMembers,
    };
  });
  const { addToast } = useToastStore((state) => ({ addToast: state.addToast }));

  const user = useMemo(() => {
    return currWorkspaceMembers.find(
      (user) => user.user_id === currWorkspace?.api_created_by,
    );
  }, [currWorkspaceMembers, currWorkspace]);

  const onClickOpenAPIDocs = () => {
    window.open(
      'https://syncly-2.gitbook.io/reference/syncly-api/version-2.0',
      '_blank',
    );
  };

  return (
    <>
      <SettingHeader
        title="API"
        rightNode={
          <Button sizeType="m" variant="outlined" onClick={onClickOpenAPIDocs}>
            Go to API document
          </Button>
        }
      />
      <APIModalWrapper>
        {currWorkspace?.api_key ? (
          <>
            <APIKeyItemWrapper>
              <Typo color={color.gray800} weight="sb">
                API Key
              </Typo>
              <div className="api-box">
                <span>{currWorkspace.api_key}</span>
                <Tooltip tooltipNode="Copy to clipboard">
                  <div
                    className="copy"
                    onClick={() => {
                      clipboardCopy(currWorkspace?.api_key || '');
                      addToast(
                        <DefaultToast
                          {...{
                            header: 'API Key is copied to clipboard',
                            Icon: (
                              <LinkOutlined
                                style={{
                                  fontSize: '16px',
                                  color: color.gray700,
                                }}
                              />
                            ),
                          }}
                        />,
                        { position: 'right' },
                      );
                    }}
                  >
                    <CopyOutlined>Copy Link</CopyOutlined>
                  </div>
                </Tooltip>
              </div>
            </APIKeyItemWrapper>
            <Space h={32} />
            <APIKeyItemWrapper>
              <Typo color={color.gray800} weight="sb">
                API Secret
              </Typo>
              <div className="api-box">
                <span>{'*'.repeat(`${currWorkspace.api_secret}`.length)}</span>
                <Tooltip tooltipNode="Copy to clipboard">
                  <div
                    className="copy"
                    onClick={() => {
                      clipboardCopy(currWorkspace.api_secret as string);
                      addToast(
                        <DefaultToast
                          {...{
                            header: 'API Secret is copied to clipboard',
                            Icon: (
                              <LinkOutlined
                                style={{
                                  fontSize: '16px',
                                  color: color.gray700,
                                }}
                              />
                            ),
                          }}
                        />,
                        { position: 'right' },
                      );
                    }}
                  >
                    <CopyOutlined />
                  </div>
                </Tooltip>
              </div>
            </APIKeyItemWrapper>
            <Space h={32} />
            <APIKeyItemWrapper>
              <Typo color={color.gray800} weight="sb">
                Created by
              </Typo>
              <div className="api-box">
                <span>
                  {user ? `${user.first_name} ${user.last_name}` : 'Unknown'}
                </span>
              </div>
            </APIKeyItemWrapper>
            <Space h={32} />
            <Flex g="8px">
              <Button
                leftIcon={
                  currWorkspace?.api_key ? <ReloadOutlined /> : <PlusOutlined />
                }
                sizeType="m"
                variant="outlined"
                onClick={() => {
                  fetchWorkspaceAPI();
                  addToast(
                    <DefaultToast
                      {...{
                        header: 'New API key created',
                        Icon: (
                          <LinkOutlined
                            style={{ fontSize: '16px', color: color.gray700 }}
                          />
                        ),
                      }}
                    />,
                    { position: 'right' },
                  );
                }}
              >
                {currWorkspace?.api_key ? 'Refresh key' : 'New key'}
              </Button>
              <Button
                leftIcon={<DeleteOutlined />}
                sizeType="m"
                variant="error_outlined"
                onClick={() => {
                  deleteWorkspaceAPI();
                  addToast(
                    <DefaultToast
                      {...{
                        header: 'API key removed',
                        Icon: (
                          <DeleteOutlined
                            style={{ fontSize: '16px', color: color.gray700 }}
                          />
                        ),
                      }}
                    />,
                    { position: 'right' },
                  );
                }}
              >
                {'Remove Key'}
              </Button>
            </Flex>
            <Space h={64} />
          </>
        ) : (
          <CreateAPIWrapper>
            <div className="inner">
              <StyledIconWrapper>
                <ApiOutlined />
              </StyledIconWrapper>
              <Typo color={color.gray600}>
                No API Key and Secret created yet
              </Typo>
              <Typo type="body2" color={color.gray600}>
                Get started by creating a new key
              </Typo>
              <Space h={32} />
              <Button
                sizeType="l"
                variant="primary"
                style={{
                  margin: 'auto',
                }}
                onClick={() => {
                  fetchWorkspaceAPI();
                  addToast(
                    <DefaultToast
                      {...{
                        header: 'New API key created',
                        Icon: (
                          <LinkOutlined
                            style={{ fontSize: '16px', color: color.gray700 }}
                          />
                        ),
                      }}
                    />,
                    { position: 'right' },
                  );
                }}
              >
                Create new key
              </Button>
            </div>
          </CreateAPIWrapper>
        )}
      </APIModalWrapper>
    </>
  );
};

export default APISettingBody;

const APIModalWrapper = styled.section`
  margin: 32px;
`;

const CreateAPIWrapper = styled.section`
  background: ${(props) => props.theme.colors.gray200};
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: 8px;
  text-align: center;
  padding: 111px 0;
`;

const StyledIconWrapper = styled.div`
  font-size: 32px;
  color: ${(props) => props.theme.colors.gray600};
  margin-bottom: 33px;
`;

const APIKeyItemWrapper = styled.section`
  .api-box {
    position: relative;
    width: 475px;
    padding: 11px 16px;
    color: ${(props) => props.theme.colors.gray800};
    background-color: ${(props) => props.theme.colors.gray200};
    border: 1px solid ${(props) => props.theme.colors.gray500};
    border-radius: 4px;
    margin-top: 8px;

    .copy {
      position: absolute;
      top: 11px;
      right: 12px;
      color: ${(props) => props.theme.colors.gray600};
    }
  }
`;
